import type {
  GetFeaturesInnerParams,
  ModalProps,
} from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures/types.ts';
import { FeatureType } from '@/app/components/search/components/EvolutionCard/utils/facilitiesTypes.ts';
import type {
  Feature,
  TooltipRenderProps,
} from '@/app/components/search/components/EvolutionCard/utils/facilitiesTypes.ts';

import {
  getDatesOnlyLabel,
  getFlexibleDatesUSPMarkup,
  renderDatesLabelWithBoldNights,
} from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures/utils/getDateUtils.tsx';
import React from 'react';
import dynamic from 'next/dynamic';
import { dynamicImportCatch } from '@/utils/appUtilsClient.tsx';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import TsxTooltip from '@/app/components/search/components/Tooltip/TsxTooltip';

export const FlexibleAlternativeDatesModal = dynamic(
  () => import('../../../../../Modal/Modals/FlexibleAlternativeDatesEvolutionModal').catch(dynamicImportCatch),
  {
    ssr: false,
  }
);

const createFlexibleDatesModal = (
  showModal: boolean,
  dealCard: GetFeaturesInnerParams['dealCard']
): Partial<Feature> => {
  if (!showModal || !dealCard) return {};

  return {
    Modal: ({ isOpen, onClose }: ModalProps) => (
      <FlexibleAlternativeDatesModal isOpen={isOpen} onClose={onClose} dealCard={dealCard} />
    ),
  };
};

const getOvernightTooltip = (nightsCount: number, t: TranslationFunction): string | undefined => {
  if (nightsCount === 1) {
    return t('vacation.oss.cards.overnight_tooltip_singular', { nights: `${nightsCount}` });
  }

  if (nightsCount > 1) {
    return t('vacation.oss.cards.overnight_tooltip', { nights: `${nightsCount}` });
  }

  return undefined;
};

export const getOvernightSearchFeatures = (params: GetFeaturesInnerParams): Feature[] => {
  const features: Feature[] = [];
  const { isMobile, t, nightsCount } = params;
  const tooltipText = getOvernightTooltip(nightsCount, t);
  const datesLabel = `${getDatesOnlyLabel(params)}`;

  features.push({
    icon: { category: 'Location', name: 'icon-building', color: 'iconPrimary' } as const,
    text: datesLabel,
    className: 'dates',
    featureType: FeatureType.normal,
    ...(tooltipText
      ? {
          Tooltip: (props: TooltipRenderProps) => (
            <TsxTooltip
              tooltip={<div>{tooltipText}</div>}
              iconSrc={{ category: 'Semantics', name: 'icon-circle-info', color: '#000000' } as const}
              {...props}
              size={isMobile ? 232 : 224}
            >
              {renderDatesLabelWithBoldNights(params)}
            </TsxTooltip>
          ),
        }
      : {}),
  });

  return features;
};

const getPartialStayTooltip = (nightsCount: number, t: TranslationFunction): string | undefined => {
  if (nightsCount) {
    return t('vacation.oss.cards.partial_stay_tooltip', { nights: `${nightsCount}` });
  }

  return undefined;
};

export const getPartialStaySearchFeatures = (params: GetFeaturesInnerParams): Feature[] => {
  const features: Feature[] = [];
  const { isMobile, t, nightsCount } = params;
  const tooltipText = getPartialStayTooltip(nightsCount, t);
  const datesLabel = `${getDatesOnlyLabel(params)}`;

  features.push({
    icon: { category: 'Location', name: 'icon-building', color: 'iconPrimary' } as const,
    text: datesLabel,
    className: 'dates',
    featureType: FeatureType.normal,
    ...(tooltipText
      ? {
          Tooltip: (props: TooltipRenderProps) => (
            <TsxTooltip
              tooltip={<div>{tooltipText}</div>}
              iconSrc={{ category: 'Semantics', name: 'icon-circle-info', color: '#000000' } as const}
              {...props}
              size={isMobile ? 232 : 224}
            >
              {renderDatesLabelWithBoldNights(params)}
            </TsxTooltip>
          ),
        }
      : {}),
  });

  return features;
};

export const getLiveSearchFeatures = (params: GetFeaturesInnerParams): Feature[] => {
  const features: Feature[] = [];

  features.push({
    icon: { category: 'Location', name: 'icon-building', color: 'iconPrimary' } as const,
    text: getDatesOnlyLabel(params),
    className: 'dates',
    featureType: FeatureType.normal,
  });

  return features;
};

export const getFlexibleFeatures = (params: GetFeaturesInnerParams): Feature[] => {
  const { dealCard, showFlexibleAlternativeDates, isOverlay, isHO } = params;

  const showFlexibleAlternativeDatesModal = showFlexibleAlternativeDates && !isOverlay;
  const features: Feature[] = [];

  const getIcon = () => {
    if (isHO && isOverlay) return undefined;
    if (isHO) return { category: 'Weather and sky', name: 'icon-moon', color: 'iconPrimary' } as const;
    return { category: 'Location', name: 'icon-building', color: 'iconPrimary' } as const;
  };

  // One row for both modes: "13 Aug 2026 (5 nights)"
  const getText = () => getFlexibleDatesUSPMarkup(params, getDatesOnlyLabel(params));

  // Regular weight either way: bold is for the partial stay and overnight rows.
  features.push({
    icon: getIcon(),
    text: getText(),
    className: showFlexibleAlternativeDates ? 'dates dates-more' : 'dates',
    featureType: FeatureType.normal,
    ...createFlexibleDatesModal(showFlexibleAlternativeDatesModal, dealCard),
  });

  return features;
};

export const getAccommodationDates = (params: GetFeaturesInnerParams): Feature[] => {
  const { datesTextWithDay } = params;

  const features: Feature[] = [];

  features.push({
    icon: { category: 'Services', name: 'icon-double-bed', color: 'iconPrimary' } as const,
    text: `${datesTextWithDay}`,
    className: 'dates dates-partial-stay',
    featureType: FeatureType.dates,
  });

  return features;
};

export const getOpxFlexDates = (params: GetFeaturesInnerParams): Feature[] => {
  const { datesTextWithDay } = params;

  const features: Feature[] = [];

  features.push({
    icon: { category: 'Office and stationery', name: 'icon-calendar-grid', color: 'iconPrimary' } as const,
    text: `${datesTextWithDay}`,
    className: 'dates opx-flex-dates',
    featureType: FeatureType.dates,
  });

  return features;
};
